import { Spacing } from '@persona-id/design-tokens';
import { Selection } from '@react-types/shared';
import { RulesLogic } from 'json-logic-js';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import useComponentLogicJsonSchema from 'dashboard/apps/InquiryTemplateEditor/hooks/useComponentLogicJsonSchema';
import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import ComponentConditionalLogicPanel from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/ComponentConditionalLogicPanel';
import {
  ComponentJsonLogicField,
  ComponentJsonLogicKey,
  ComponentJsonLogicRecord,
  ComponentJsonLogicRule,
  LogicRuleSection,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import {
  formatJSONLogicRule,
  getInvertedLogicKey,
  isConfigJSONLogic,
  isInvertedLogicKey,
  isInvertedRuleConfig,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import SectionAccordionCondensed from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordionCondensed';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import { LogicBlockOrV2 } from 'dashboard/lib/workflows';
import { InquiryTemplateVersionStepUi } from 'dashboard/models/inquiry-template-version';
import ListBox from 'design-system/components/ListBox';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { UiComponentConfig } from 'lib/interfaces/ui-components';

const CREATE_NEW_LOGIC_RULE_KEY: NewJsonLogicRuleKey = 'json-logic-editor__new-logic-rule';
export const LOGIC_SECTION_TEXT: Record<LogicRuleSection, { title: string; description: string }> =
  {
    client: {
      title: 'On screen update',
      description:
        'Logic rules are evaluated in real time and can only reference inputs on the current screen.',
    },
    server: {
      title: 'On screen load',
      description:
        'Logic rules are evaluated only when the screen loads and can reference any field configured on the template.',
    },
  };

const getDefaultLogicRuleName = (
  jsonLogicKey: string,
  jsonLogicField: ComponentJsonLogicField,
): string => {
  const splitJsonLogicKey = jsonLogicKey.split('.');
  // top level json logic key
  if (splitJsonLogicKey.length === 1) {
    return isInvertedLogicKey(jsonLogicKey as ComponentJsonLogicKey)
      ? jsonLogicField.invertedRuleName
      : jsonLogicField.ruleName;
  } else {
    const [namespace, index, logicKey] = splitJsonLogicKey;
    return `${namespace} (${Number(index) + 1}) ${isInvertedLogicKey(logicKey as ComponentJsonLogicKey) ? jsonLogicField.invertedRuleName : jsonLogicField.ruleName}`;
  }
};

const getDisabledText = (
  jsonLogicFields: ComponentJsonLogicRecord,
  section: LogicRuleSection,
): string | undefined => {
  if (
    Object.values(jsonLogicFields)
      .filter((logicField) => logicField.section === section)
      .every((logicField) => logicField.logic != null && logicField.logic !== false)
  ) {
    return 'All logic attributes have configured rules set.';
  }
};

type NewJsonLogicRuleKey = 'json-logic-editor__new-logic-rule';
type LogicRuleConfig = {
  key: ComponentJsonLogicKey | NewJsonLogicRuleKey;
  section: LogicRuleSection;
};

interface ComponentJsonLogicEditorProps {
  step: InquiryTemplateVersionStepUi;
  componentConfig: UiComponentConfig;
  jsonLogicFields: ComponentJsonLogicRecord;
  editable: boolean;
  onJSONUpdate: (updates: Record<string, RulesLogic>) => void;
}

const ComponentJsonLogicEditor = ({
  step,
  componentConfig,
  jsonLogicFields,
  editable,
  onJSONUpdate,
}: ComponentJsonLogicEditorProps) => {
  const hasAccessForEnhancedTier = useHasAccessForTierDeterminator()(
    EditorAccessTier.InquiriesEnhanced,
  );
  const componentJsonSchema = useComponentLogicJsonSchema(step, componentConfig);
  const [selectedLogicRuleConfig, setSelectedLogicRuleConfig] = useState<LogicRuleConfig | null>(
    null,
  );

  const { key, section } = selectedLogicRuleConfig || {};

  const { rules, isInvertedRule } = useMemo<{
    rules: LogicBlockOrV2;
    isInvertedRule: boolean;
  }>(() => {
    const ruleToFormat =
      key != null && key !== CREATE_NEW_LOGIC_RULE_KEY ? jsonLogicFields[key].logic : null;
    return formatJSONLogicRule(ruleToFormat as ComponentJsonLogicRule);
  }, [jsonLogicFields, key]);

  const serverLogicFields = Object.fromEntries(
    Object.entries(jsonLogicFields).filter(
      ([_, jsonLogicRecord]) => jsonLogicRecord.section === 'server',
    ),
  );
  const clientLogicFields = Object.fromEntries(
    Object.entries(jsonLogicFields).filter(
      ([_, jsonLogicRecord]) => jsonLogicRecord.section === 'client',
    ),
  );

  const getLogicKeyOptions = useCallback(
    (section: LogicRuleSection) => {
      return Object.entries(jsonLogicFields)
        .filter(([_, jsonLogicRecord]) => jsonLogicRecord.section === section)
        .flatMap(([logicKey, jsonLogicRecord]) => {
          const invertedLogicKey = getInvertedLogicKey(logicKey as ComponentJsonLogicKey);
          const logicRuleConfigured =
            jsonLogicRecord.logic != null && jsonLogicRecord.logic !== false;
          // disable both key and inverted key when adding a new rule or editing a rule for a different logic key,
          // but allow the key/inverted key to be selected when editing its configured logic rule.
          const hasRuleAndIsNotSelected = logicRuleConfigured && logicKey !== key;
          return [
            {
              key: logicKey,
              label: getDefaultLogicRuleName(logicKey, jsonLogicRecord),
              disabled: (logicKey === key && !isInvertedRule) || hasRuleAndIsNotSelected,
            },
            {
              key: invertedLogicKey,
              label: getDefaultLogicRuleName(invertedLogicKey, jsonLogicRecord),
              disabled: (logicKey === key && isInvertedRule) || hasRuleAndIsNotSelected,
            },
          ];
        });
    },
    [isInvertedRule, key, jsonLogicFields],
  );

  const handleUpdateConditional = useCallback(
    (
      conditional: LogicBlockOrV2 | { '!': LogicBlockOrV2 } | null,
      logicKey: ComponentJsonLogicKey | null,
    ) => {
      /** when creating a new rule, don't dispatch the update until the logic key is specified */
      if (logicKey == null) return;
      const logicKeyUpdated = key != null && key !== CREATE_NEW_LOGIC_RULE_KEY && logicKey !== key;

      const componentConfigUpdates: Record<string, any> = { [logicKey]: conditional };
      /** also set the original logic attribute to null when the key is changed from the panel */
      if (logicKeyUpdated) {
        componentConfigUpdates[key] = null;
      }

      onJSONUpdate(componentConfigUpdates);

      /** update selected config when rule is created or logic key is updated */
      if ((logicKeyUpdated || key === CREATE_NEW_LOGIC_RULE_KEY) && section != null) {
        setSelectedLogicRuleConfig({ key: logicKey, section });
      }

      /** remove selected logic rule config when logic rule is deleted */
      if (conditional == null) {
        setSelectedLogicRuleConfig(null);
      }
    },
    [key, section, onJSONUpdate],
  );

  return (
    <>
      <LogicSection
        logicRuleSection='server'
        title={LOGIC_SECTION_TEXT.server.title}
        description={LOGIC_SECTION_TEXT.server.description}
        disabledText={getDisabledText(jsonLogicFields, 'server')}
        logicRules={serverLogicFields}
        selectedKey={key}
        editable={editable}
        hasAccessForEnhancedTier={hasAccessForEnhancedTier}
        onLogicKeySelected={setSelectedLogicRuleConfig}
      />
      <LogicSection
        logicRuleSection='client'
        title={LOGIC_SECTION_TEXT.client.title}
        description={LOGIC_SECTION_TEXT.client.description}
        disabledText={getDisabledText(jsonLogicFields, 'client')}
        logicRules={clientLogicFields}
        selectedKey={key}
        editable={editable}
        hasAccessForEnhancedTier={hasAccessForEnhancedTier}
        onLogicKeySelected={setSelectedLogicRuleConfig}
      />
      {key != null && section != null && (
        <ComponentConditionalLogicPanel
          title={LOGIC_SECTION_TEXT[section].title}
          description={LOGIC_SECTION_TEXT[section].description}
          selectedLogicKey={key !== CREATE_NEW_LOGIC_RULE_KEY ? key : null}
          conditional={{
            rules,
          }}
          logicRuleSection={section}
          jsonSchema={componentJsonSchema[section]}
          logicKeyOptions={getLogicKeyOptions(section)}
          isInvertedRule={isInvertedRule}
          editable={editable && hasAccessForEnhancedTier}
          onOutsideClick={() => setSelectedLogicRuleConfig(null)}
          updateConditional={handleUpdateConditional}
        />
      )}
    </>
  );
};

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[3]};
`;

const EmptyStateButton = styled.button`
  ${paragraphMixins.md};
  width: 100%;
  text-align: start;
  color: ${({ theme }) => theme.color.text.secondary};
  padding: ${Spacing[1]} ${Spacing[2]};
  background-color: transparent;
  outline: none;
  border: none;
  &:hover:not(&:disabled) {
    cursor: pointer;
  }
`;

interface LogicSectionProps {
  title: string;
  description: string;
  disabledText?: string;
  logicRuleSection: LogicRuleSection;
  logicRules: ComponentJsonLogicRecord;
  selectedKey: ComponentJsonLogicKey | NewJsonLogicRuleKey | undefined;
  editable: boolean;
  hasAccessForEnhancedTier: boolean;
  onLogicKeySelected: (logicRuleConfig: LogicRuleConfig) => void;
}

const LogicSection = ({
  title,
  description,
  disabledText,
  logicRuleSection,
  logicRules,
  selectedKey,
  hasAccessForEnhancedTier,
  editable,
  onLogicKeySelected,
}: LogicSectionProps) => {
  const isEditable = editable && hasAccessForEnhancedTier;
  const items: Array<{ name: string; key: ComponentJsonLogicKey | NewJsonLogicRuleKey }> =
    Object.entries(logicRules)
      // ATM we filter out non-JSON logic values, but the longer term fix is to update the
      // control UI to handle boolean AND JSON logic values, similar to the UI for 'required'
      .filter(([_, logicField]) => isConfigJSONLogic(logicField.logic))
      .map(([key, logicField]) => ({
        name: getDefaultLogicRuleName(
          isInvertedRuleConfig(logicField.logic as ComponentJsonLogicRule)
            ? getInvertedLogicKey(key as ComponentJsonLogicKey)
            : key,
          logicField,
        ),
        key: key as ComponentJsonLogicKey,
      }));

  const handleLogicRuleAdded = useCallback(
    () => onLogicKeySelected({ key: CREATE_NEW_LOGIC_RULE_KEY, section: logicRuleSection }),
    [logicRuleSection, onLogicKeySelected],
  );

  const handleLogicKeySelected = useCallback(
    (selection: Selection) => {
      if (selection !== 'all') {
        const key = selection.values().next().value as ComponentJsonLogicKey | NewJsonLogicRuleKey;
        onLogicKeySelected({ key, section: logicRuleSection });
      }
    },
    [logicRuleSection, onLogicKeySelected],
  );

  return (
    <SectionAccordionCondensed
      title={
        <HorizontalContainer>
          {!hasAccessForEnhancedTier && (
            <ProductUpsellTooltip
              side='left'
              learnMoreId='component-panel__json-logic-editor'
              accessTier={EditorAccessTier.InquiriesEnhanced}
              trigger={
                <div>
                  <InquiriesGatingSticker
                    dataMarketingIdSuffix='json-logic-editor'
                    stickerType={EnhancedGatingStickerType.Enhanced}
                    iconOnly
                  />
                </div>
              }
            />
          )}
          {title}
        </HorizontalContainer>
      }
    >
      <Paragraph>{description}</Paragraph>
      <Spacer height={2} />
      <Label color='primary' htmlFor={`${title}-logic-section__listbox`}>
        Logic rules
      </Label>
      <Spacer height={0.5} />
      <ListBox
        id={`${title}-logic-section__listbox`}
        aria-label={`Logic rules for ${title}`}
        selectionMode='single'
        selectionBehavior='toggle'
        items={items}
        disabledKeys={isEditable ? [] : items.map((item) => item.key)}
        selectedKeys={selectedKey ? [selectedKey] : []}
        renderEmptyState={() => (
          /** disabled text would only pertain to empty jsonSchema if empty state is shown */
          <EmptyStateButton
            as='button'
            type='button'
            id={`${title}-logic-section__empty-state-button`}
            aria-disabled={!isEditable || disabledText != null}
            disabled={!isEditable || disabledText != null}
            onClick={handleLogicRuleAdded}
          >
            {!hasAccessForEnhancedTier
              ? 'No logic rules configured'
              : disabledText != null
                ? 'Add values to configure rules'
                : 'Click to add a new rule'}
          </EmptyStateButton>
        )}
        onAdd={isEditable && disabledText == null ? handleLogicRuleAdded : undefined}
        onSelectionChange={handleLogicKeySelected}
        disallowEmptySelection
        fullWidth
      >
        {items.map((item) => (
          <ListBox.Item key={item.key} id={item.key} aria-label={`Listbox item for ${item.name}`}>
            {item.name}
          </ListBox.Item>
        ))}
      </ListBox>
      {disabledText != null && isEditable && (
        <Paragraph style={{ marginTop: '8px', paddingLeft: '4px' }}>{disabledText}</Paragraph>
      )}
    </SectionAccordionCondensed>
  );
};

export default ComponentJsonLogicEditor;
